import { Controller } from "stimulus";
import Rails from "@rails/ujs";

export default class extends Controller {
  static targets = [
    "actions",
    "header",
    "selectAll",
    "counter",
    "spinner",
    "item",
    "dropdown",
    "progress",
    "dismissAction",
    "restoreAction",
  ];

  connect() {
    this.hideActions();
    this.itemClicked();
  }

  itemClicked() {
    const count = this.selectedItems.length;

    if (count > 0) {
      this.showActions(count);
    } else {
      this.hideActions();
    }
  }

  updateActionVisibility() {
    if (!this.hasDismissActionTarget || !this.hasRestoreActionTarget) return;

    const selectedItems = this.itemTargets.filter(item => item.checked);
    const dismissedItems = selectedItems.filter(item => item.dataset.dismissed === "true");
    const activeItems = selectedItems.filter(item => item.dataset.dismissed === "false");

    const hasMixedSelection = dismissedItems.length > 0 && activeItems.length > 0;

    // Check current dismissal status filter from URL or form
    const statusFilter = this.getCurrentStatusFilter();

    // Show/hide buttons based on status filter
    if (statusFilter === "active") {
      // On Active page: only show Dismiss, never Restore
      this.dismissActionTarget.style.display = "flex";
      this.restoreActionTarget.style.display = "none";
    } else if (statusFilter === "dismissed") {
      // On Dismissed page: only show Restore, never Dismiss
      this.dismissActionTarget.style.display = "none";
      this.restoreActionTarget.style.display = "flex";
    } else {
      // On All page: show both buttons
      this.dismissActionTarget.style.display = "flex";
      this.restoreActionTarget.style.display = "flex";
    }

    // Apply enable/disable logic for shown buttons
    if (this.dismissActionTarget.style.display === "flex") {
      this.dismissActionTarget.disabled = activeItems.length === 0 || hasMixedSelection;
      if (activeItems.length === 0) {
        this.dismissActionTarget.title = "Cannot dismiss - no active items selected";
      } else if (hasMixedSelection) {
        this.dismissActionTarget.title =
          "Cannot dismiss - mixed selection includes already dismissed items";
      } else {
        this.dismissActionTarget.title = "";
      }
    }

    if (this.restoreActionTarget.style.display === "flex") {
      this.restoreActionTarget.disabled = dismissedItems.length === 0 || hasMixedSelection;
      if (dismissedItems.length === 0) {
        this.restoreActionTarget.title = "Cannot restore - no dismissed items selected";
      } else if (hasMixedSelection) {
        this.restoreActionTarget.title = "Cannot restore - mixed selection includes active items";
      } else {
        this.restoreActionTarget.title = "";
      }
    }
  }

  getCurrentStatusFilter() {
    // Try to get status from URL parameters first
    const urlParams = new URLSearchParams(window.location.search);
    const dismissalStatus = urlParams.get("dismissal_status");

    if (dismissalStatus) {
      return dismissalStatus;
    }

    // Fall back to checking the select element if it exists
    const statusSelect = document.querySelector("#dismissal_status");

    if (statusSelect) {
      return statusSelect.value;
    }

    // Default to 'all' if we can't determine the status
    return "all";
  }

  actionClicked(event) {
    const action = event.currentTarget;

    action.disabled = true;
    this.showSpinner();

    if ("url" in action.dataset) {
      fetch(action.dataset.url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": Rails.csrfToken(),
        },
        body: JSON.stringify({
          bulk_ids: this.selectedItems,
        }),
      }).then(resp => {
        this.hideSpinner();

        if (resp.status >= 400) {
          alert("Something went wrong performing this action."); // eslint-disable-line
          action.disabled = false;
        }

        Turbolinks.visit(window.location);
      });
    } else if ("jobUrl" in action.dataset) {
      this.showProgress();
      fetch(action.dataset.jobUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": Rails.csrfToken(),
        },
        body: JSON.stringify({
          bulk_ids: this.selectedItems,
        }),
      })
        .then(blob => blob.json())
        .then(response => {
          this.intervalId = setInterval(() => {
            fetch(response.status_url, {
              headers: {
                Accept: "application/json",
              },
            })
              .then(blob => blob.json())
              .then(resp => {
                if (resp.progress > 0) {
                  this.progressTarget.innerText = `${resp.progress}%`;
                }

                if (resp.error) {
                  alert("Something went wrong performing this action."); // eslint-disable-line
                  this.hideSpinner();
                  this.hideProgress();
                  this.clearInterval(this.intervalId);
                  action.disabled = false;
                } else if (resp.progress === 100) {
                  this.hideSpinner();
                  this.hideProgress();
                  this.clearInterval(this.intervalId);
                  Turbolinks.visit(window.location);
                }
              });
          }, 1000);
        });
    }
  }

  selectAllClicked() {
    if (this.selectAllTarget.indeterminate) {
      this.selectAllTarget.checked = true;
    }

    this.itemTargets.forEach(item => {
      item.checked = this.selectAllTarget.checked;
    });

    this.itemClicked();
  }

  showActions(count) {
    this.actionsTarget.classList.remove("hidden");
    this.counterTarget.innerHTML = `${count} selected...`;
    this.headerTarget.classList.add("hidden");
    this.syncSelectAllState();
    // Apply status-based button visibility whenever actions are shown
    this.updateActionVisibility();
  }

  syncSelectAllState() {
    if (this.selectedItems.length === this.totalItems) {
      this.selectAllTarget.indeterminate = false;
      this.selectAllTarget.checked = true;
    } else if (this.selectedItems.length === 0) {
      this.selectAllTarget.indeterminate = false;
      this.selectAllTarget.checked = false;
    } else {
      this.selectAllTarget.indeterminate = true;
    }
  }

  hideActions() {
    this.actionsTarget.classList.add("hidden");
    this.headerTarget.classList.remove("hidden");
    this.dropdownTargets.forEach(d => d.classList.add("hidden"));
  }

  showSpinner() {
    this.spinnerTarget.classList.remove("hidden");
  }

  showProgress() {
    this.progressTarget.classList.remove("hidden");
  }

  hideSpinner() {
    this.spinnerTarget.classList.add("hidden");
  }

  hideProgress() {
    this.progressTarget.classList.add("hidden");
  }

  get selectedItems() {
    return this.itemTargets.filter(item => item.checked).map(item => item.value);
  }

  get totalItems() {
    return this.itemTargets.length;
  }

  clearInterval = () => clearInterval(this.intervalId);
}
